import type { PopoverProps as AriaPopoverProps } from "react-aria-components"

/**
 * Arrow-center distance from the menu corner on corner-aligned menus: a 1rem visual gap
 * plus half the arrow's 24px width.
 */
export const ARROW_CORNER_INSET = 28

export interface ArrowGeometry {
  /** The axis the menu slides along relative to the trigger: "x" for top/bottom sides, "y" for left/right. */
  crossAxis: "x" | "y"
  /**
   * Popover cross-axis offset that slides the menu so the trigger's center — where the
   * arrow points — sits exactly {@link ARROW_CORNER_INSET} from the menu's corner.
   * Undefined for centered placements or when the trigger wasn't measured.
   */
  crossOffset: number | undefined
  /**
   * Cross-axis distance walking the arrow from React Aria's anchor point (which marks
   * the trigger's near edge on corner-aligned placements) back to the trigger's center.
   */
  nudge: number
}

/**
 * Offsets that make the popover arrow point at the center of the trigger on
 * corner-aligned placements — e.g. "bottom right" aligns the right corners, "left top"
 * the top ones. -1/+1 alignment signs point toward the left/top and right/bottom
 * corners; 0 is centered, where the anchor already marks the trigger's center and no
 * offsets are needed. LTR assumed.
 *
 * Pure math over the placement string and a trigger measurement, so the DOM read stays
 * with the caller. (If react-aria-components is upgraded to ≥1.19, `getTargetRect` can
 * replace this.)
 */
export const computeArrowGeometry = (
  placement: NonNullable<AriaPopoverProps["placement"]>,
  triggerRect: { width: number; height: number } | undefined,
): ArrowGeometry => {
  const [side, alignment] = placement.split(" ")
  const crossAxis: "x" | "y" = side === "left" || side === "right" ? "y" : "x"
  const alignSign =
    alignment === "left" || alignment === "start" || alignment === "top"
      ? -1
      : alignment === "right" || alignment === "end" || alignment === "bottom"
        ? 1
        : 0

  if (alignSign === 0 || !triggerRect) return { crossAxis, crossOffset: undefined, nudge: 0 }

  const crossSize = crossAxis === "x" ? triggerRect.width : triggerRect.height
  return {
    crossAxis,
    crossOffset: alignSign * (ARROW_CORNER_INSET - crossSize / 2),
    nudge: -alignSign * (crossSize / 2),
  }
}
